import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import Popup from '../../../../../components/common-components/pop-up/Popup';
import { ArrowLeftRight, Building2, Tag } from 'lucide-react';
import { useStore } from '../../../../../hooks/useStore';
import { FormikDropdown } from '../../../../../components/form-components/common-components/dropdown/FormikDropdown';
import FormPopupFooter from '../../../../../components/form-components/common-components/FormPopupFooter';
import styles from './SwitchHospital.module.css';
import {
  SwitchHospitalFormValues,
  switchHospitalInitialValues,
} from '../../../../../models/hospital/HospitalModel';
import { SwitchHospitalSchema } from '../../../../../schemas/SwitchHospitalSchema';

const HospitalTagSync = () => {
  const { values, setFieldValue } = useFormikContext<SwitchHospitalFormValues>();
  const { hospitalStore, hospitalFilesStore } = useStore();

  useEffect(() => {
    if (values.hospitalId) {
      const selectedHospital = hospitalStore.hospitalList.find(h => h.id === values.hospitalId);
      if (selectedHospital) {
        hospitalFilesStore.getTagsByHospitalId(selectedHospital.id);
        setFieldValue('tagId', null);
      }
    }
  }, [values.hospitalId, hospitalStore.hospitalList, hospitalFilesStore, setFieldValue]);

  return null;
};

const SwitchHospital = observer(() => {
  const { popupStore, hospitalFilesStore, hospitalStore, uiStore } = useStore();

  const tagOptions = useMemo(
    () =>
      hospitalFilesStore.tags.map(tag => ({
        value: tag.id,
        label: tag.prefix,
      })),
    [hospitalFilesStore.tags]
  );

  useEffect(() => {
    const fetchHospitals = async () => {
      if (!popupStore.isSwitchHospitalOpen) {
        return;
      }

      try {
        uiStore.setLoading(true);
        await hospitalStore.listAllHospitals();
      } catch (err) {
        console.error('Error fetching hospitals:', err);
      } finally {
        uiStore.setLoading(false);
      }
    };
    fetchHospitals();
  }, [hospitalStore, uiStore, popupStore.isSwitchHospitalOpen]);

  const hospitalOptions = useMemo(
    () =>
      hospitalStore.hospitalList
        .filter(
          (hospital): hospital is { id: number; hospitalName: string } =>
            hospital.id !== undefined && hospital.hospitalName !== undefined
        )
        .map(hospital => ({
          value: hospital.id,
          label: hospital.hospitalName,
        })),
    [hospitalStore.hospitalList]
  );

  const getInitialValues = (): SwitchHospitalFormValues => {
    if (popupStore.isSwitchHospitalOpen) {
      return {
        hospitalId: hospitalFilesStore.selectedHospital?.id || null,
        tagId: hospitalFilesStore.selectedTag?.id || null,
      };
    }
    return switchHospitalInitialValues;
  };

  const handleSubmit = async (values: SwitchHospitalFormValues) => {
    if (!values.hospitalId || !values.tagId) {
      return;
    }

    const selectedHospital = hospitalStore.hospitalList.find(h => h.id === values.hospitalId);
    const selectedTag = hospitalFilesStore.tags.find(tag => tag.id === values.tagId);

    if (selectedHospital && selectedTag) {
      hospitalFilesStore.setSelectedHospital(selectedHospital);
      hospitalFilesStore.setSelectedTag(selectedTag);
      popupStore.setIsSwitchHospitalOpen(false);
    }
  };

  const handleClose = () => {
    popupStore.setIsSwitchHospitalOpen(false);
  };

  if (uiStore.isLoading) {
    return null;
  }

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={SwitchHospitalSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values }) => (
        <Popup
          isOpen={popupStore.isSwitchHospitalOpen}
          onClose={handleClose}
          title="Hastane Seçimi"
          description="Çalışılacak hastane ve etiketi seçin"
          variant="info"
          icon={<ArrowLeftRight size={22} strokeWidth={2} />}
          footer={() => <FormPopupFooter onClose={handleClose} />}
        >
          <div className={styles.container}>
            <Form className={styles.form}>
              <HospitalTagSync />
              <div className={styles.dropdownsContainer}>
                <FormikDropdown
                  name="hospitalId"
                  options={hospitalOptions}
                  label="Hastane Seçiniz"
                  placeholder="Hastane seçin..."
                  startIcon={<Building2 size={18} strokeWidth={2} />}
                />
                <FormikDropdown
                  name="tagId"
                  options={tagOptions}
                  label="Etiket No Seçiniz"
                  placeholder="Etiket seçin..."
                  disabled={!values.hospitalId}
                  startIcon={<Tag size={18} strokeWidth={2} />}
                />
              </div>
            </Form>
          </div>
        </Popup>
      )}
    </Formik>
  );
});

export default SwitchHospital;
